import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { receptors } from '../../data/caprelsa';
const XS = [62, 170, 278];
const MEMBRANE_Y = 92;
const PURPLE = '#3d2785';
const GOLD = '#d6a530';
const ORANGE = '#e5862d';
const LILAC = '#e4def0';
const MONO = 'IBM Plex Mono, monospace';

/** Schematic of vandetanib inhibiting the intracellular kinase domains of RET, VEGFR and EGFR. */
export function MoaVisual({
  inhibited,
  still = false
}: {
  inhibited: boolean;
  still?: boolean;
}) {
  const reduced = useReducedMotion() || still;
  return <svg viewBox="0 0 340 250" className="h-full w-full" role="img" aria-label={`Schematic: RET, VEGFR and EGFR receptor tyrosine kinases ${inhibited ? 'inhibited by vandetanib' : 'signalling downstream'}`} data-id="element-552" data-mp="a9pltp9mnjxp">
      <text x="6" y="14" fill={PURPLE} fillOpacity="0.5" fontSize="7" fontFamily={MONO} letterSpacing="1" data-id="element-553" data-mp="2gycapeg7tba7">EXTRACELLULAR</text>
      <text x="6" y="126" fill={PURPLE} fillOpacity="0.5" fontSize="7" fontFamily={MONO} letterSpacing="1" data-id="element-554" data-mp="2kz5xg4hx8k1i">INTRACELLULAR</text>

      {/* Membrane bilayer */}
      <rect x="0" y={MEMBRANE_Y - 8} width="340" height="16" fill="#f1edf8" data-id="element-555" data-mp="2mm4ncyygox0u" />
      {Array.from({
      length: 34
    }).map((_, i) => <g key={i} data-id="element-556" data-mp="2ctyuzo4asdl4" data-mpi={i}>
          <circle cx={5 + i * 10} cy={MEMBRANE_Y - 8} r="2.6" fill={GOLD} fillOpacity="0.55" data-id="element-557" data-mp="2cvjp5cmp9uig" />
          <circle cx={5 + i * 10} cy={MEMBRANE_Y + 8} r="2.6" fill={GOLD} fillOpacity="0.55" data-id="element-558" data-mp="27wgkvuejnsbj" />
        </g>)}

      {receptors.map((r, i) => {
      const x = XS[i];
      return <g key={r.id} data-id="element-559" data-mp="30jt9090cs86y" data-mpi={i}>
            <path d={`M${x - 4} ${MEMBRANE_Y - 8} L${x - 4} 50 L${x - 16} 26`} stroke={PURPLE} strokeWidth="5" strokeLinecap="round" fill="none" data-id="element-560" data-mp="30km6skhc3ncy" />
            <path d={`M${x + 4} ${MEMBRANE_Y - 8} L${x + 4} 50 L${x + 16} 26`} stroke={PURPLE} strokeWidth="5" strokeLinecap="round" fill="none" data-id="element-561" data-mp="2qm5cbdj6sqm5" />
            <text x={x} y="18" textAnchor="middle" fill={PURPLE} fontSize="11" fontWeight="800" fontFamily="Inter, sans-serif" data-id="element-562" data-mp="1k6mbxnxcinwk">
              {r.label}
            </text>
            <rect x={x - 7} y={MEMBRANE_Y - 9} width="14" height="18" rx="3" fill={PURPLE} fillOpacity="0.8" data-id="element-563" data-mp="77170ij3upg1" />
            <motion.rect x={x - 22} y={MEMBRANE_Y + 14} width="44" height="26" rx="8" initial={false} animate={{
          fill: inhibited ? '#ffffff' : ORANGE,
          stroke: inhibited ? PURPLE : ORANGE
        }} strokeWidth="1.4" transition={{
          duration: 0.4
        }} data-id="element-564" data-mp="b7uu59oq407v" />
            <text x={x} y={MEMBRANE_Y + 30} textAnchor="middle" fill={inhibited ? PURPLE : '#ffffff'} fontSize="7" fontWeight="600" fontFamily={MONO} letterSpacing="0.8" data-id="element-565" data-mp="3ofxy8xwp5zo1">
              KINASE
            </text>

            <AnimatePresence data-id="element-566" data-mp="3fmb6rjzivikn">
              {inhibited && <motion.g initial={{
            opacity: 0,
            y: 70,
            x: (170 - x) * 0.4
          }} animate={{
            opacity: 1,
            y: 0,
            x: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: still ? 0 : 0.7,
            delay: still ? 0 : i * 0.12,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-567" data-mp="3fkqddxoy25od">
                  <polygon points={hexPoints(x + 18, MEMBRANE_Y + 14, 7)} fill={GOLD} stroke="#ffffff" strokeWidth="1.5" data-id="element-568" data-mp="2i21ew13aroow" />
                </motion.g>}
            </AnimatePresence>

            <line x1={x} x2={x} y1={MEMBRANE_Y + 42} y2="200" stroke={inhibited ? LILAC : ORANGE} strokeOpacity={inhibited ? 1 : 0.4} strokeDasharray="2 4" data-id="element-569" data-mp="2r1okkz327gnr" />
            {!inhibited && [0, 1, 2].map(k => <motion.circle key={k} cx={x} r="3" fill={ORANGE} initial={{
          cy: MEMBRANE_Y + 44,
          opacity: 0
        }} animate={reduced ? {
          cy: MEMBRANE_Y + 70 + k * 30,
          opacity: 0.9
        } : {
          cy: [MEMBRANE_Y + 44, 198],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          delay: k * 0.53,
          ease: 'linear'
        }} data-id="element-570" data-mp="30zcclh9a6a28" />)}

            <motion.g initial={false} animate={{
          opacity: inhibited ? 0.4 : 1
        }} data-id="element-571" data-mp="1h4rid2h8kovc">
              <text x={x} y="220" textAnchor="middle" fill="#2b1c62" fontSize="8.5" fontWeight="600" fontFamily="Inter, sans-serif" data-id="element-572" data-mp="1m3b5v7bha8pt">
                {r.effect.split(' ').slice(0, -1).join(' ')}
              </text>
              <text x={x} y="232" textAnchor="middle" fill="#2b1c62" fontSize="8.5" fontWeight="600" fontFamily="Inter, sans-serif" data-id="element-573" data-mp="1vtnp8w7k3vud">
                {r.effect.split(' ').slice(-1)}
              </text>
            </motion.g>
            {inhibited && <motion.text x={x} y="246" textAnchor="middle" fill={PURPLE} fontSize="9" fontWeight="700" fontFamily={MONO} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: still ? 0 : 0.6
        }} data-id="element-574" data-mp="134p4drc1ufld">
                ↓ INHIBITED
              </motion.text>}
          </g>;
    })}
    </svg>;
}
function hexPoints(cx: number, cy: number, r: number) {
  return Array.from({
    length: 6
  }).map((_, i) => {
    const a = Math.PI / 3 * i + Math.PI / 6;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');
}